import getCommonsNorthboundAPIs from '../contexts/dependencyManager/utils/getCommonsNorthboundAPIs';
import * as url from '../api/api-endpoints';
import { jarvisHttpClient } from './web-http-client';

import { api } from '../config';

import {
  DeviceCategory,
  PostDeviceMappingRequestResponse,
  PostDeviceMappingRequestPayload,
  GetDeviceMappingRequestResponse
} from '../types/devices';
import { User } from '../types/user';
import {
  DeviceTelemetryRequest,
  PostDeviceTelemetryPayload
} from '../types/device-telemetry';

import {
  deserializer,
  serializeToJSONAPIPayload
} from '@eu-data-act/react-euda-commons/dist/index';
import { getMaxAgeFromCacheControl } from '../utils/common';

export const getUser = async (): Promise<User> => {
  const { v1 } = getCommonsNorthboundAPIs();

  const response = await v1?.userInterface?._userData.get();
  const user = { ...response };
  return user as User;
};

export const getDeviceCategories = async (): Promise<DeviceCategory[]> => {
  const { httpClient, defaultHeaders } = jarvisHttpClient();
  const response = await httpClient.get({
    url: url.GET_DEVICE_CATEGORIES(),
    headers: defaultHeaders
  });

  const deserializedResponse = deserializer(
    api.jsonapi.types.categories,
    response.data
  );

  return deserializedResponse as DeviceCategory[];
};

export const postDeviceMapping = async (
  payload: PostDeviceMappingRequestPayload
): Promise<PostDeviceMappingRequestResponse> => {
  const { httpClient, defaultHeaders } = jarvisHttpClient();
  const jsonApiRequestPayload = serializeToJSONAPIPayload(
    payload,
    api.jsonapi.types.deviceMapping
  );

  const response = await httpClient.post({
    url: url.POST_DEVICE_MAPPING_REQUESTS(),
    data: jsonApiRequestPayload,
    headers: defaultHeaders
  });

  const deserializedResponse = deserializer(
    api.jsonapi.types.deviceMapping,
    response.data
  );

  return deserializedResponse as PostDeviceMappingRequestResponse;
};

export const getDeviceMapping = async (payload: {
  id: string;
}): Promise<GetDeviceMappingRequestResponse | undefined> => {
  const { httpClient, defaultHeaders } = jarvisHttpClient();
  const response = await httpClient.get({
    url: url.GET_DEVICE_MAPPING_REQUEST(payload),
    headers: defaultHeaders
  });

  const maxAge = getMaxAgeFromCacheControl(response.headers);

  const deserializedResponse = deserializer(
    api.jsonapi.types.deviceMapping,
    response.data
  ) as GetDeviceMappingRequestResponse | undefined;

  if (maxAge && deserializedResponse) {
    deserializedResponse.maxAge = maxAge;
  }

  return deserializedResponse;
};

export const postDeviceTelemetry = async (
  payload: PostDeviceTelemetryPayload
): Promise<DeviceTelemetryRequest> => {
  const { httpClient, defaultHeaders } = jarvisHttpClient();
  const jsonApiRequestPayload = serializeToJSONAPIPayload(
    payload,
    api.jsonapi.types.deviceTelemetry
  );

  const response = await httpClient.post({
    url: url.POST_DEVICE_TELEMETRY_REQUESTS(),
    data: jsonApiRequestPayload as Record<string, unknown>,
    headers: defaultHeaders
  });

  const deserializedResponse = deserializer(
    api.jsonapi.types.deviceTelemetry,
    response.data
  );

  return deserializedResponse as DeviceTelemetryRequest;
};

export const getDeviceTelemetry = async (): Promise<
  DeviceTelemetryRequest[]
> => {
  const { httpClient, defaultHeaders } = jarvisHttpClient();
  const response = await httpClient.get({
    url: url.GET_DEVICE_TELEMETRY_REQUESTS(),
    headers: defaultHeaders
  });

  const deserializedResponse = deserializer(
    api.jsonapi.types.deviceTelemetry,
    response.data
  );

  return deserializedResponse as DeviceTelemetryRequest[];
};
